import { useSyncExternalStore } from 'react';

/**
 * Seconds remaining until `deadlineIso`, updated every 500ms against one
 * shared clock across the whole app.
 *
 * A shared store rather than a per-component `setInterval` for two reasons.
 * The retract countdown runs in two places at once — the live command view
 * and the raise dialog — so per-component intervals would drift by up to
 * half a second between them, and a "Retract · 47s" that reads 46 on one
 * screen and 47 on another is worse than either number alone. And the tab
 * naturally throttles background timers; one shared clock is one place to
 * reason about that, rather than many that each fire at their own rate.
 *
 * Returns:
 *
 *   - `null` — the deadline is nil or already in the past. Callers hide the
 *     countdown UI in this case; do not claim seconds you don't have.
 *   - a positive integer — the number of whole seconds remaining. Uses
 *     `Math.ceil` so "1s" is shown until the deadline actually passes
 *     rather than snapping to zero mid-second.
 *
 * The countdown text's ARIA role is deliberately not decided here —
 * `<span role="status">` is right for a passive live value, `aria-live`
 * settings are the caller's choice.
 */

// The store. One 500ms interval for the whole page.
let now = Date.now();
const listeners = new Set<() => void>();
let intervalId: ReturnType<typeof setInterval> | null = null;

function ensureTicking() {
  if (intervalId !== null || listeners.size === 0) return;
  intervalId = setInterval(() => {
    now = Date.now();
    for (const l of listeners) l();
  }, 500);
}

function stopIfIdle() {
  if (intervalId !== null && listeners.size === 0) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  ensureTicking();
  return () => {
    listeners.delete(cb);
    stopIfIdle();
  };
}

function getSnapshot() {
  return now;
}

/**
 * A pure snapshot for SSR and the first paint before subscription resolves.
 * `Date.now()` on the server would produce a value the client mismatch-warns
 * about, so ship a stable zero: it renders as "no countdown", which is the
 * safe default for a screen that has not yet asked what time it is.
 */
function getServerSnapshot() {
  return 0;
}

export function useCountdown(deadlineIso: string | null | undefined): number | null {
  const clock = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  if (!deadlineIso) return null;
  const deadline = new Date(deadlineIso).getTime();
  if (!Number.isFinite(deadline)) return null;
  // Guard against a first-paint clock of 0 (server snapshot): treat that as
  // "no answer yet" rather than a countdown of decades.
  if (clock === 0) return null;
  const remaining = Math.ceil((deadline - clock) / 1000);
  return remaining > 0 ? remaining : null;
}
